// src/ui/modals/VenueModal.js
import { actions } from "../../app/state.js";
import { createVenue, updateVenue, deleteVenue, getVenueById } from "../../services/venuesService.js";
import { getSupabaseClient } from "../../services/supabaseClient.js";
import { VALID_REGIONS, normalizeRegion, formatRegionLabel } from "../../constants/regions.js";
import { onSafeBackdropClick } from "../modalUtils.js";

const $ = (root, sel) => root.querySelector(sel);
const norm = (v) => (v == null ? "" : String(v).trim());

function escapeHtml(str) {
  return String(str ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function closeSelf(root) {
  window.CRM?.modals?.closeModal?.(root);
}

function showErrors(root, errors) {
  const box = $(root, "#venue-modal-errors");
  const ul = $(root, "#venue-modal-errors-list");
  if (!box || !ul) return;
  ul.innerHTML = "";
  if (!errors?.length) {
    box.classList.add("hidden");
    return;
  }
  for (const e of errors) {
    const li = document.createElement("li");
    li.textContent = e;
    ul.appendChild(li);
  }
  box.classList.remove("hidden");
}

function setEditMode(root, isEditable) {
  const inputs = root.querySelectorAll("input, select, textarea");
  inputs.forEach((el) => {
    el.disabled = !isEditable;
    if (!isEditable) el.classList.add("bg-slate-50", "cursor-not-allowed");
    else el.classList.remove("bg-slate-50", "cursor-not-allowed");
  });

  const saveBtn = $(root, "#venue-modal-save");
  if (saveBtn) saveBtn.classList.toggle("hidden", !isEditable);

  const deleteBtn = $(root, "#venue-modal-delete");
  const mode = $(root, "#venue-modal-mode").textContent;
  if (deleteBtn && mode === "edit") {
    deleteBtn.classList.toggle("hidden", !isEditable);
  }

  const editToggle = $(root, "#venue-modal-edit-toggle");
  if (editToggle) {
    editToggle.classList.toggle("hidden", isEditable);
  }
}

function fillRegions(root, selectId, selectedValue = null) {
  const sel = $(root, selectId);
  if (!sel) return;

  const currentVal = (selectedValue || "").toLowerCase().replace(/_/g, " ");

  sel.innerHTML = "";
  const def = document.createElement("option");
  def.value = "";
  def.textContent = "—";
  sel.appendChild(def);

  for (const r of VALID_REGIONS) {
    const opt = document.createElement("option");
    opt.value = r;
    opt.textContent = formatRegionLabel(r);
    if (r === currentVal) opt.selected = true;
    sel.appendChild(opt);
  }
}

function wireTabs(root) {
  const tabs = root.querySelectorAll("#venue-modal-tabs button");
  const tabContents = {
    datos: root.querySelector("#venue-modal-tab-datos"),
    eventos: root.querySelector("#venue-modal-tab-eventos"),
  };

  tabs.forEach(btn => {
    btn.addEventListener("click", () => {
      const target = btn.dataset.tab;

      tabs.forEach(t => {
        if (t === btn) {
          t.classList.add("border-blue-500", "text-blue-600");
          t.classList.remove("border-transparent", "text-slate-500", "hover:text-slate-700", "hover:border-slate-300");
        } else {
          t.classList.remove("border-blue-500", "text-blue-600");
          t.classList.add("border-transparent", "text-slate-500", "hover:text-slate-700", "hover:border-slate-300");
        }
      });

      Object.entries(tabContents).forEach(([key, contentEl]) => {
        if (!contentEl) return;
        if (key === target) {
          contentEl.classList.remove("hidden");
          contentEl.classList.add("block");
        } else {
          contentEl.classList.add("hidden");
          contentEl.classList.remove("block");
        }
      });
    });
  });

  // Init to 'datos'
  const firstBtn = root.querySelector('#venue-modal-tabs button[data-tab="datos"]');
  if (firstBtn) firstBtn.click();
}

function readForm(root) {
  // OJO: tu tabla venues tiene: name, address, province, city, contact_notes, ops_notes, pricing_notes, team_id...
  // Tu HTML tiene: region/capacity/notes -> los mapeamos a campos existentes.
  const name = norm($(root, "#venue-name")?.value);
  const address = norm($(root, "#venue-address")?.value) || null;

  // region: normalizada
  const rawRegion = $(root, "#venue-region")?.value;
  const region = normalizeRegion(rawRegion); // retorna null si inválido

  const capacity = norm($(root, "#venue-capacity")?.value);
  const notes = norm($(root, "#venue-notes")?.value);

  // ops_notes <- notes + capacity
  const ops_notes =
    [notes ? `Notas: ${notes}` : null, capacity ? `Aforo: ${capacity}` : null]
      .filter(Boolean)
      .join("\n") || null;

  return { name, address, region, ops_notes };
}

function validate(values) {
  const errs = [];
  if (!values.name) errs.push("El nombre es obligatorio.");
  const rawRegion = document.querySelector("#venue-region")?.value; // hack para chequear si el user seleccionó algo pero falló la normalización
  // (aunque si seleccionó del dropdown, será válido).

  // Si user manipula HTML o hay valor legacy seleccionado que no matchea:
  if (rawRegion && !values.region && rawRegion !== "") {
    // Esto pasa si normalizeRegion devuelve null para un string no vacío.
    errs.push(`La región "${rawRegion}" no es válida.`);
  }
  return errs;
}

export async function openCreateVenueModal({ teamId, initialName, onSaved } = {}) {
  if (!teamId) throw new Error("teamId es obligatorio para crear venue.");

  const root = window.CRM?.modals?.openModal?.("venue-modal-template");
  if (!root) {
    actions.addToast?.({ type: "error", message: "No se pudo abrir el modal de venue." });
    return;
  }

  // UI
  $(root, "#venue-modal-title").textContent = "Nuevo venue";
  $(root, "#venue-modal-subtitle").textContent = "Completa los datos del venue.";
  $(root, "#venue-modal-mode").textContent = "create";
  $(root, "#venue-modal-id").textContent = "—";
  $(root, "#venue-modal-delete")?.classList.add("hidden");
  $(root, "#venue-modal-edit-toggle")?.classList.add("hidden");

  setEditMode(root, true);

  const nameInput = $(root, "#venue-name");
  if (nameInput) nameInput.value = initialName || "";
  const addressInput = $(root, "#venue-address");
  if (addressInput) addressInput.value = "";
  const capacityInput = $(root, "#venue-capacity");
  if (capacityInput) capacityInput.value = "";
  const notesInput = $(root, "#venue-notes");
  if (notesInput) notesInput.value = "";

  // Populate regions
  fillRegions(root, "#venue-region");

  wireTabs(root);
  const eventosTab = root.querySelector('#venue-modal-tabs button[data-tab="eventos"]');
  if (eventosTab) eventosTab.classList.add("hidden");

  // close/cancel
  $(root, "#venue-modal-close")?.addEventListener("click", () => closeSelf(root));
  $(root, "#venue-modal-cancel")?.addEventListener("click", () => closeSelf(root));
  onSafeBackdropClick(root, () => closeSelf(root));

  // save
  $(root, "#venue-modal-save")?.addEventListener("click", async () => {
    const values = readForm(root);
    const errors = validate(values);
    showErrors(root, errors);
    if (errors.length) return;

    try {
      const created = await createVenue({
        team_id: teamId,
        name: values.name,
        address: values.address,
        region: values.region,
        ops_notes: values.ops_notes,
      });

      actions.addToast?.({ type: "success", message: "Venue creado" });
      closeSelf(root);
      onSaved?.(created);
    } catch (e) {
      actions.addToast?.({ type: "error", message: e?.message || String(e) });
    }
  });

  showErrors(root, []);
}

export async function openEditVenueModal(venueId, { teamId, onSaved } = {}) {
  if (!venueId) return;

  const root = window.CRM?.modals?.openModal?.("venue-modal-template");
  if (!root) {
    actions.addToast?.({ type: "error", message: "No se pudo abrir el modal de venue." });
    return;
  }

  // UI
  $(root, "#venue-modal-title").textContent = "Editar venue";
  $(root, "#venue-modal-subtitle").textContent = "Edita los datos del venue.";
  $(root, "#venue-modal-mode").textContent = "edit";
  $(root, "#venue-modal-id").textContent = venueId;

  // close/cancel
  $(root, "#venue-modal-close")?.addEventListener("click", () => closeSelf(root));
  $(root, "#venue-modal-cancel")?.addEventListener("click", () => closeSelf(root));
  onSafeBackdropClick(root, () => closeSelf(root));

  // Start fully editable
  setEditMode(root, true);
  try {
    const v = await getVenueById(venueId);
    $(root, "#venue-name").value = v.name || "";
    $(root, "#venue-address").value = v.address || "";
    root.dataset.originalAddress = v.address || "";

    // Mapeo inverso de region (simple aproximación si coincide con los values del select)
    fillRegions(root, "#venue-region", v.region);

    $(root, "#venue-notes").value = v.ops_notes || "";

    wireTabs(root);
    const eventosTab = root.querySelector('#venue-modal-tabs button[data-tab="eventos"]');
    if (eventosTab) eventosTab.classList.remove("hidden");

    // Eventos relacionados
    const evList = $(root, "#venue-modal-events-list");
    const countBadge = $(root, "#venue-modal-events-count");
    if (evList) {
      evList.innerHTML = "<li class='text-xs italic'>Cargando eventos...</li>";
      const supabase = getSupabaseClient();
      // Fetch events from the join table. The events table stores the title as client_name_legacy.
      const { data: evData, error: evError } = await supabase
        .from("event_venues")
        .select(`event_id, events ( id, client_name_legacy, date )`)
        .eq("venue_id", venueId);

      if (evError) throw evError;

      if (!evData || evData.length === 0) {
        if (countBadge) countBadge.textContent = "0";
        evList.innerHTML = "<li class='text-xs italic px-1'>No hay eventos asociados a este venue.</li>";
      } else {
        if (countBadge) countBadge.textContent = evData.length.toString();

        evList.innerHTML = evData.map(ev => {
          const e = ev.events;
          if (!e) return "";
          const dateStr = e.date ? new Date(e.date).toLocaleDateString("es-ES") : "Sin fecha";
          let stageColor = "bg-slate-100 text-slate-500";
          if (e.stage === "confirmed" || e.stage === "done") stageColor = "bg-emerald-100 text-emerald-700";
          if (e.stage === "rechazado") stageColor = "bg-red-100 text-red-700";

          return `<li class="activity-item flex items-center justify-between border border-slate-100 hover:border-slate-300 hover:bg-slate-50 hover:shadow-sm transition-all rounded-xl p-3 cursor-pointer mb-2 last:mb-0" data-event-id="${e.id}">
               <div class="flex-1 min-w-0 pr-2">
                 <div class="font-bold text-slate-800 truncate">${escapeHtml(e.client_name_legacy || 'Evento sin nombre')}</div>
                 <div class="text-xs text-slate-500 mt-0.5">${dateStr}</div>
               </div>
               <span class="text-[10px] whitespace-nowrap px-2 py-1 rounded-full font-bold uppercase tracking-tighter ${stageColor}">${e.stage || 'lead'}</span>
             </li>`;
        }).join("");

        // Click handler for opening the Event details
        evList.querySelectorAll(".activity-item").forEach(item => {
          item.addEventListener("click", () => {
            if (window.CRM?.modals?.openEditEventModal) {
              window.CRM.modals.openEditEventModal(item.dataset.eventId);
            } else {
              // Fallback literal import in case it's not registered globally
              import("./EventModal.js").then(module => {
                module.openEditEventModal(item.dataset.eventId);
              }).catch(err => {
                actions.addToast?.({ type: "error", message: "No se pudo abrir el evento." });
              });
            }
          });
        });
      }
    }

  } catch (e) {
    actions.addToast?.({ type: "error", message: e?.message || String(e) });
  }

  // save
  $(root, "#venue-modal-save")?.addEventListener("click", async () => {
    const values = readForm(root);
    const errors = validate(values);
    showErrors(root, errors);
    if (errors.length) return;

    try {
      const addressChanged = (values.address || "") !== (root.dataset.originalAddress || "");

      await updateVenue(venueId, {
        name: values.name,
        address: values.address,
        region: values.region,
        ops_notes: values.ops_notes,
        // Si cambia la dirección, invalidamos el cache de km para que se
        // recalcule la próxima vez que se seleccione el venue.
        ...(addressChanged ? { lat: null, lng: null, km_from_origin: null } : {}),
      });

      actions.addToast?.({ type: "success", message: "Venue actualizado" });
      closeSelf(root);
      onSaved?.({ id: venueId });
    } catch (e) {
      actions.addToast?.({ type: "error", message: e?.message || String(e) });
    }
  });

  // delete
  $(root, "#venue-modal-delete")?.addEventListener("click", async () => {
    try {
      await deleteVenue(venueId);
      actions.addToast?.({ type: "success", message: "Venue eliminado" });
      closeSelf(root);
      onSaved?.({ id: venueId, deleted: true });
    } catch (e) {
      actions.addToast?.({ type: "error", message: e?.message || String(e) });
    }
  });

  showErrors(root, []);
}
